import { DateTime } from "luxon";
import React, {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { IconType } from "react-icons";
import { FaChevronRight, FaTv } from "react-icons/fa6";
import { FiSmartphone } from "react-icons/fi";
import { LuZoomIn, LuZoomOut } from "react-icons/lu";
import { RiPlayLine, RiStopLine } from "react-icons/ri";
import {
  RecordingSessionType,
  NewRecordingSessionType,
  SessionItemType,
} from "../__generated__/graphql";
import { DEVICE_TYPES } from "../utils/constants";
import { DeviceType, LoadedRecording } from "../utils/interfaces";
import { isMac } from "../utils/navigator";
import IconButton from "./IconButton";
import RecordingsTimeline, { Region } from "./RecordingsTimeline";
import RecordingsZoomOverviewTimeline from "./RecordingsZoomOverviewTimeline";
import TimePointDisplayIndividual from "./TimePointDisplayIndividual";
import VideoPlayerRuler from "./VideoPlayerRuler";

const DEFAULT_REGION = { START: 0.35, END: 0.65 };
const MAX_SCALE = 2048;
const ZOOM_IN_KEYS = ["+", "="];
const ZOOM_OUT_KEYS = ["-", "_"];

interface Device {
  device: DeviceType;
  Icon: IconType;
  recordings: RecordingSessionType[] | SessionItemType[];
}

interface GroupedRecordingsTimelineProps {
  currentPosition?: number;
  currentSection?: [number, number] | null;
  displayOnly?: boolean;
  enhanced?: boolean;
  hoverTime?: number | null;
  loadedRecordings?: LoadedRecording[];
  recordings: NewRecordingSessionType | RecordingSessionType[];
  selected?: DeviceType | false | null;
  selectedRegion?: Region | null;
  timeline: [number, number];
  timezone?: string;
  onPlay: (device: DeviceType) => void;
  onTimelineClick?: (recording: RecordingSessionType, time: number) => void;
  setSelectedRegion?: Dispatch<SetStateAction<Region | null>>;
  setTimezone?: Dispatch<SetStateAction<string>>;
}

const GroupedRecordingsTimeline: React.FC<GroupedRecordingsTimelineProps> = ({
  currentPosition,
  currentSection,
  displayOnly,
  enhanced,
  hoverTime,
  loadedRecordings = [],
  recordings,
  selected,
  selectedRegion,
  timeline,
  timezone = DateTime.local().zoneName,
  onPlay,
  onTimelineClick,
  setSelectedRegion,
  setTimezone = () => {},
}) => {
  const [scale, setScale] = useState(1);
  const [offset, setOffset] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [zoomDragStart, setZoomDragStart] = useState<number | null>(null);
  const [zoomDragEnd, setZoomDragEnd] = useState<number | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const contentRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const startXRef = useRef(0);
  const offsetStartRef = useRef(0);
  const isMoving = useRef(false);

  const desktopRecordings = useMemo(() => {
    if (
      "sessionItems" in recordings &&
      Array.isArray(recordings.sessionItems)
    ) {
      return recordings.sessionItems.filter(
        (r: SessionItemType) => !r.deviceInfo?.isMobile,
      );
    }
    return (recordings as RecordingSessionType[]).filter(
      (r: RecordingSessionType) => !r.metadata?.device?.isMobile,
    );
  }, [recordings]);
  const mobileRecordings = useMemo(() => {
    if (
      "sessionItems" in recordings &&
      Array.isArray(recordings.sessionItems)
    ) {
      return recordings.sessionItems.filter(
        (r: SessionItemType) => r.deviceInfo?.isMobile,
      );
    }
    return (recordings as RecordingSessionType[]).filter(
      (r: RecordingSessionType) => r.metadata?.device?.isMobile,
    );
  }, [recordings]);

  const totalMs = timeline[1] - timeline[0];

  const [visibleStart, visibleEnd] = useMemo(() => {
    if (!enhanced || !containerRef.current || scale === 1) return timeline;

    const containerWidth = containerRef.current.offsetWidth - 76;
    const visibleMs = totalMs / scale;
    const msPerPixel = visibleMs / containerWidth;
    const centerShiftMs = offset * msPerPixel;
    const centerTime = timeline[0] + visibleMs / 2 - centerShiftMs;

    return [centerTime - visibleMs / 2, centerTime + visibleMs / 2];
  }, [enhanced, offset, scale, timeline, totalMs]);

  const handleZoomDirection = useCallback(
    (direction: "in" | "out", multiplier = 2) => {
      setScale((prevScale) => {
        const container = containerRef.current;
        if (!container) return prevScale;

        const newScale =
          direction === "out"
            ? Math.max(1, prevScale / multiplier)
            : Math.min(MAX_SCALE, prevScale * multiplier);

        if (newScale === prevScale) return newScale;

        const m =
          direction === "out" ? prevScale / newScale : newScale / prevScale;

        const containerWidth = container.offsetWidth - 76;
        const maxOffset = (newScale - 1) * containerWidth;

        setOffset((prevOffset) => {
          const newOffset =
            direction === "out"
              ? (prevOffset + (containerWidth * (m - 1)) / 2) / m
              : m * prevOffset - (containerWidth * (m - 1)) / 2;
          return Math.max(-maxOffset, Math.min(newOffset, 0));
        });

        return newScale;
      });
    },
    [],
  );

  const resetZoom = useCallback(() => {
    setScale(1);
    setOffset(0);
  }, []);

  // Auto-select a region when displayOnly becomes false
  useEffect(() => {
    if (!displayOnly && setSelectedRegion && visibleStart && visibleEnd) {
      const regionStart =
        visibleStart + (visibleEnd - visibleStart) * DEFAULT_REGION.START;
      const regionEnd =
        visibleStart + (visibleEnd - visibleStart) * DEFAULT_REGION.END;
      setSelectedRegion({ start: regionStart, end: regionEnd });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayOnly]);

  // Reset zoom on recordings change
  useEffect(() => {
    if (scale !== 1) {
      resetZoom();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recordings]);

  // Handle zoom
  useEffect(() => {
    const handleZoomKey = (e: KeyboardEvent) => {
      if (!enhanced) return;
      if (
        (e.ctrlKey || e.metaKey) &&
        e.shiftKey &&
        (ZOOM_IN_KEYS.includes(e.key) || ZOOM_OUT_KEYS.includes(e.key))
      ) {
        e.preventDefault();
        const direction = ZOOM_OUT_KEYS.includes(e.key) ? "out" : "in";
        handleZoomDirection(direction);
      }
    };

    const handleWheel = (e: WheelEvent) => {
      if (!enhanced) return;

      const hasZoomModifiers = (e.ctrlKey || e.metaKey) && e.shiftKey;

      if (!hasZoomModifiers) return;

      let delta: number | null = null;

      if (isMac()) {
        // Accept either deltaX or deltaY if they're significant
        delta = Math.abs(e.deltaY) > Math.abs(e.deltaX) ? e.deltaY : e.deltaX;
      } else {
        // On non-mac, stick with vertical scroll
        if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
          delta = e.deltaY;
        }
      }

      e.preventDefault();
      if (delta !== null) {
        const direction = delta > 0 ? "out" : "in";
        handleZoomDirection(direction, 1.01);
      }
    };

    window.addEventListener("keydown", handleZoomKey);
    window.addEventListener("wheel", handleWheel, { passive: false });

    return () => {
      window.removeEventListener("keydown", handleZoomKey);
      window.removeEventListener("wheel", handleWheel);
    };
  }, [enhanced, handleZoomDirection]);

  // Handle timeline drag
  useEffect(() => {
    // TODO A switch between pan and zoom modes should be added
    return;
    const elements = Object.values(contentRefs.current);
    if (!enhanced || scale === 1 || elements.length === 0) return;

    const handleMouseDown = (e: MouseEvent) => {
      const clickedInsideSelectedRegion =
        e.target instanceof Node &&
        document.getElementById("selected-region")?.contains(e.target);

      if (clickedInsideSelectedRegion && !displayOnly) return;

      setIsDragging(true);
      startXRef.current = e.clientX;
      offsetStartRef.current = offset;
    };

    elements.forEach((el) =>
      el?.addEventListener("mousedown", handleMouseDown),
    );
    return () => {
      elements.forEach((el) =>
        el?.removeEventListener("mousedown", handleMouseDown),
      );
    };
  }, [enhanced, scale, offset, displayOnly]);

  useEffect(() => {
    // TODO A switch between pan and zoom modes should be added
    return;
    const handleMouseMove = (e: MouseEvent) => {
      if (!isDragging) return;
      isMoving.current = true;
      const delta = e.clientX - startXRef.current;
      const containerWidth = containerRef.current?.offsetWidth
        ? containerRef.current.offsetWidth - 76
        : 0;
      const newOffset = offsetStartRef.current + delta;
      const maxOffset = (scale - 1) * containerWidth;
      setOffset(Math.max(-maxOffset, Math.min(newOffset, 0)));
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      // Use a timeout so at first onTimelineClick happen then isMoving is marked as false
      setTimeout(() => {
        isMoving.current = false;
      }, 0);
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isDragging, scale]);

  // Handle zoom in by selecting
  useEffect(() => {
    const elements = Object.values(contentRefs.current);
    if (!enhanced || elements.length === 0 || !containerRef.current) return;

    const container = containerRef.current;

    const handleMouseDown = (e: MouseEvent) => {
      // Ignore right-click (button 2)
      if (e.button === 2) return;

      if (e.shiftKey) {
        resetZoom();
        return;
      }
      const clickedInsideSelectedRegion =
        e.target instanceof Node &&
        document.getElementById("selected-region")?.contains(e.target);

      if (clickedInsideSelectedRegion && !displayOnly) return;

      const rect = container.getBoundingClientRect();
      setZoomDragStart(e.clientX - rect.left - 48); // subtract icon sidebar
      setZoomDragEnd(null);
    };

    elements.forEach((el) =>
      el?.addEventListener("mousedown", handleMouseDown),
    );
    return () => {
      elements.forEach((el) =>
        el?.removeEventListener("mousedown", handleMouseDown),
      );
    };
  }, [enhanced, displayOnly, resetZoom]);

  useEffect(() => {
    if (!enhanced || !containerRef.current) return;

    const container = containerRef.current;

    const handleMouseMove = (e: MouseEvent) => {
      if (zoomDragStart === null) return;

      const rect = container.getBoundingClientRect();
      isMoving.current = true;
      const px = e.clientX - rect.left - 48;
      const clampedX = Math.max(0, Math.min(container.offsetWidth - 76, px));
      setZoomDragEnd(clampedX);
    };

    const handleMouseUp = () => {
      if (
        zoomDragStart !== null &&
        zoomDragEnd !== null &&
        Math.abs(zoomDragEnd - zoomDragStart) > 4
      ) {
        const containerWidth = container.offsetWidth - 76;
        const totalMs = timeline[1] - timeline[0];
        const visibleMs = totalMs / scale;
        const msPerPixel = visibleMs / containerWidth;
        const visibleStart = timeline[0] + -offset * msPerPixel;

        const dragStartTime = visibleStart + zoomDragStart * msPerPixel;
        const dragEndTime = visibleStart + zoomDragEnd * msPerPixel;

        const zoomStart = Math.min(dragStartTime, dragEndTime);
        const zoomEnd = Math.max(dragStartTime, dragEndTime);

        const newScale = totalMs / (zoomEnd - zoomStart);
        const newCenter = (zoomStart + zoomEnd) / 2;
        const newOffset =
          (timeline[0] + totalMs / newScale / 2 - newCenter) *
          (containerWidth / (totalMs / newScale));

        setScale(newScale);
        setOffset(
          Math.max(-(newScale - 1) * containerWidth, Math.min(newOffset, 0)),
        );
      }

      setZoomDragStart(null);
      setZoomDragEnd(null);

      // Use a timeout so at first onTimelineClick happen then isMoving is marked as false
      setTimeout(() => {
        isMoving.current = false;
      }, 0);
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [enhanced, offset, scale, timeline, zoomDragEnd, zoomDragStart]);

  const devices: Device[] = [
    { device: DEVICE_TYPES.DESKTOP, Icon: FaTv, recordings: desktopRecordings },
    {
      device: DEVICE_TYPES.MOBILE,
      Icon: FiSmartphone,
      recordings: mobileRecordings,
    },
  ];

  const intl = Intl as typeof Intl & {
    supportedValuesOf: (key: string) => string[];
  };

  return (
    <div
      className='flex flex-col flex-1 gap-0.5 text-xs'
      ref={containerRef}
      data-testid='recordingsTimeLineContainer'
    >
      <div className='flex gap-1'>
        <div className='flex flex-col gap-0.5 w-full'>
          {devices.map(({ device, Icon, recordings }) => (
            <div
              key={device}
              className={`flex gap-1 focus:outline-none focus:ring-0 focus:border-transparent ${
                enhanced && selected === device ? "h-10" : "h-3"
              }`}
              {...(!enhanced &&
                recordings.length && {
                  onClick: () => onPlay(device),
                  role: "button",
                  tabIndex: 0,
                })}
              data-testid={device}
            >
              {selected === device ? (
                <RiStopLine
                  className='text-primary-blue-100 border border-sky-200 rounded'
                  data-testid='stop'
                />
              ) : (
                <RiPlayLine
                  className={`border rounded ${
                    recordings.length
                      ? "text-primary-blue-100 border-sky-200 cursor-pointer"
                      : "text-zinc-200 border-zinc-200"
                  }`}
                  onClick={() => recordings.length && onPlay(device)}
                  data-testid='play'
                />
              )}
              <Icon className='text-zinc-500' data-testid='deviceIcon' />
              <FaChevronRight
                className='text-zinc-500'
                data-testid='faChevronRight'
              />
              <RecordingsTimeline
                loadedRecordings={loadedRecordings}
                recordings={recordings}
                timeline={timeline}
                currentPosition={
                  enhanced && selected === device ? currentPosition : 0
                }
                {...(enhanced && selected === device
                  ? { onTimelineClick }
                  : {})}
                scale={scale}
                offset={offset}
                isDraggable={false}
                isMoving={isMoving.current}
                isSelectable={enhanced}
                contentRef={(el) => (contentRefs.current[device] = el)}
                displayOnly={displayOnly}
                selectedRegion={selectedRegion}
                setSelectedRegion={setSelectedRegion}
                hoverTime={hoverTime}
                currentSection={currentSection}
                visibleStart={visibleStart}
                visibleEnd={visibleEnd}
                zoomDragStart={zoomDragStart}
                zoomDragEnd={zoomDragEnd}
                resetZoom={resetZoom}
              />
            </div>
          ))}
        </div>
        {enhanced && (
          <div className='flex flex-col gap-1'>
            <IconButton
              onClick={() => handleZoomDirection("in")}
              size='text-sm'
              flag
              icon={LuZoomIn}
              dataTestId='zoomIn'
              className='!w-6 !h-6'
              disabled={scale === MAX_SCALE}
            />
            <IconButton
              onClick={() => handleZoomDirection("out")}
              size='text-sm'
              flag
              icon={LuZoomOut}
              dataTestId='zoomOut'
              className='!w-6 !h-6'
              disabled={scale === 1}
            />
          </div>
        )}
      </div>

      {enhanced && !!containerRef.current?.offsetWidth && (
        <div className='ml-12'>
          <TimePointDisplayIndividual
            containerWidth={containerRef.current.offsetWidth - 76}
            visibleStart={visibleStart}
            visibleEnd={visibleEnd}
            offset={offset}
            scale={scale}
            timezone={timezone}
          />

          <div className='flex text-xs mt-1 mb-2'>
            <select
              value={timezone}
              onChange={(e) => setTimezone(e.target.value)}
              className='text-xs border border-gray-300 rounded px-2 py-0.5 bg-white'
              data-testid='timezoneSelector'
            >
              {intl.supportedValuesOf("timeZone").map((tz) => {
                const offset = DateTime.now().setZone(tz).offset;
                const gmt =
                  offset === 0
                    ? "GMT"
                    : `GMT${offset > 0 ? "+" : ""}${(offset / 60).toFixed(0)}`;
                return (
                  <option
                    key={tz}
                    value={tz}
                    data-testid={`timezoneValue${tz.replaceAll("/", "_")}`}
                  >
                    {tz} ({gmt})
                  </option>
                );
              })}
            </select>
          </div>
          <VideoPlayerRuler
            containerWidth={containerRef.current.offsetWidth - 48}
            timeline={timeline}
            timezone={timezone}
          />
          <RecordingsZoomOverviewTimeline
            containerRef={containerRef}
            currentPosition={currentPosition}
            timeline={timeline}
            timezone={timezone}
            visibleStart={visibleStart}
            visibleEnd={visibleEnd}
            setOffset={setOffset}
            setScale={setScale}
          />
        </div>
      )}
    </div>
  );
};

export default GroupedRecordingsTimeline;
